import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './WordPressHosting.css';

const WordPressHosting = () => {
    const scrollToPricing = (e) => {
        e.preventDefault();
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            const headerOffset = 100;
            const elementPosition = pricingSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>WordPress Hosting - Fast, Managed & Secure | Epicfinal</title>
                <meta name="title" content="WordPress Hosting - Fast, Managed & Secure | Epicfinal" />
                <meta name="description" content="Managed WordPress hosting from $5.99/mo. Auto-updates, NVMe SSD, free SSL, Cloudflare CDN, malware protection, 1-click install & expert WordPress support. Optimized for speed & security." />
                <meta name="keywords" content="WordPress hosting, managed WordPress, WordPress hosting plans, WP hosting, WordPress cloud hosting, fast WordPress hosting, secure WordPress hosting, WordPress SSD hosting, WordPress managed hosting, WooCommerce hosting, WordPress multisite hosting, WordPress blog hosting" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/wordpress-hosting" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/wordpress-hosting" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/wordpress-hosting" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/wordpress-hosting" />
                <meta property="og:title" content="Managed WordPress Hosting - Fast, Secure & Optimized | Epicfinal" />
                <meta property="og:description" content="Managed WordPress hosting from $5.99/mo. Auto-updates, NVMe SSD storage, free SSL, Cloudflare CDN & expert WordPress support." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal WordPress Hosting - Managed & Optimized" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/wordpress-hosting" />
                <meta name="twitter:title" content="Managed WordPress Hosting - Fast & Secure | Epicfinal" />
                <meta name="twitter:description" content="Managed WordPress hosting from $5.99/mo. Auto-updates, NVMe SSD, free SSL & expert support." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "WordPress Hosting - Fast, Managed & Secure",
                        "description": "Managed WordPress hosting with automatic updates, NVMe SSD storage, free SSL, Cloudflare CDN, and expert WordPress support.",
                        "url": "https://epicfinal.com/wordpress-hosting",
                        "provider": {
                            "@type": "Organization",
                            "name": "Epicfinal",
                            "url": "https://epicfinal.com"
                        },
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Home",
                                    "item": "https://epicfinal.com"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "WordPress Hosting",
                                    "item": "https://epicfinal.com/wordpress-hosting"
                                }
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Epicfinal WordPress Hosting",
                        "description": "Managed WordPress hosting with automatic updates, enhanced security, daily backups, and expert WordPress support",
                        "brand": {
                            "@type": "Brand",
                            "name": "Epicfinal"
                        },
                        "category": "Web Hosting",
                        "offers": [
                            {
                                "@type": "Offer",
                                "name": "WordPress Starter",
                                "price": "5.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-starter"
                            },
                            {
                                "@type": "Offer",
                                "name": "WordPress Pro",
                                "price": "8.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-pro"
                            },
                            {
                                "@type": "Offer",
                                "name": "WordPress Business",
                                "price": "15.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-business"
                            }
                        ],
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "reviewCount": "200",
                            "bestRating": "5",
                            "worstRating": "1"
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "What is managed WordPress hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Managed WordPress hosting is a premium hosting service specifically optimized for WordPress. It includes automatic updates for WordPress core, themes, and plugins, enhanced security, daily backups, and expert WordPress support."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What's included in Epicfinal WordPress hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "All WordPress hosting plans include NVMe SSD storage, automatic WordPress updates, free SSL certificates, Cloudflare CDN integration, WAF protection, malware scanning, daily backups, 1-click WordPress install, caching for faster page loads, and expert WordPress support."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Is WordPress hosting faster than shared hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, WordPress hosting is optimized specifically for WordPress performance. It includes built-in caching, optimized server configurations, and Cloudflare CDN integration for faster page load times compared to generic shared hosting."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does WordPress hosting include WooCommerce support?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, our WordPress Business plan is ideal for WooCommerce stores, with support for WordPress Multisite setups and enough resources for e-commerce websites with up to 150,000 monthly visits."
                                }
                            }
                        ]
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "WordPress",
                        "applicationCategory": "WebApplication",
                        "operatingSystem": "All",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "USD"
                        }
                    }
                `}</script>
            </Helmet>

            <div className="wp-hosting-page">
                {/* Hero Section */}
                <section className="wp-hero">
                    <div className="container">
                        <div className="wp-hero-content">
                            <div className="wp-logo-badge">
                                <i className="lab la-wordpress"></i>
                            </div>
                            <h1 className="wp-hero-title">
                                WordPress Hosting
                            </h1>
                            <p className="wp-hero-subtitle">
                                Managed hosting optimized for WordPress
                            </p>
                            <p className="wp-hero-description">
                                Launch your WordPress site with lightning-fast performance, automatic updates, and expert support. Everything you need to succeed.
                            </p>
                            <div className="wp-hero-buttons">
                                <a href="#pricing" onClick={scrollToPricing} className="btn-wp btn-primary">
                                    View Plans & Pricing
                                </a>
                                <Link to="/contact1" className="btn-wp btn-secondary">
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="wp-features">
                    <div className="container">
                        <div className="wp-section-header">
                            <h2>WordPress Optimized Features</h2>
                            <p>Everything WordPress needs to perform at its best</p>
                        </div>
                        <div className="wp-features-grid">
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-bolt"></i>
                                </div>
                                <h3>Lightning Fast</h3>
                                <p>Optimized servers with built-in caching for blazing-fast WordPress performance.</p>
                            </div>
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-sync"></i>
                                </div>
                                <h3>Auto Updates</h3>
                                <p>Automatic WordPress core, theme, and plugin updates keep your site secure.</p>
                            </div>
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h3>Enhanced Security</h3>
                                <p>Advanced security measures including malware scanning and firewall protection.</p>
                            </div>
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-database"></i>
                                </div>
                                <h3>Daily Backups</h3>
                                <p>Automatic daily backups with easy one-click restore functionality.</p>
                            </div>
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-user-shield"></i>
                                </div>
                                <h3>Expert Support</h3>
                                <p> WordPress expert support ready to help with any issue.</p>
                            </div>
                            <div className="wp-feature-card">
                                <div className="wp-feature-icon">
                                    <i className="las la-rocket"></i>
                                </div>
                                <h3>1-Click Install</h3>
                                <p>Install WordPress in seconds with our automated setup process.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Plans Section */}
                <section className="wp-pricing" id="pricing">
                    <div className="container">
                        <div className="wp-section-header">
                            <h2>WordPress Hosting Plans</h2>
                            <p>Optimized plans for WordPress websites</p>
                        </div>
                        <div className="wp-pricing-grid">
                            {/* Starter Plan */}
                            <div className="wp-pricing-card">
                                <div className="wp-pricing-header">
                                    <h3>WordPress Starter</h3>
                                    <p className="wp-pricing-tagline">Designed for small businesses, freelancers and personal projects</p>
                                </div>
                                <div className="wp-pricing-price">
                                    <span className="wp-price-amount">$5.99</span>
                                    <span className="wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>1 Website</strong></li>
                                    <li><i className="las la-check"></i> 5GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> Email & Databases</li>
                                    <li><i className="las la-check"></i> Up to 3 Databases</li>
                                    <li><i className="las la-check"></i> WAF – Web Application Firewall</li>
                                    <li><i className="las la-check"></i> Malware Protection</li>
                                    <li><i className="las la-check"></i> Free SSL Certificates</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> User-Friendly Control Panel</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-starter" 
                                   className="wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Pro Plan */}
                            <div className="wp-pricing-card featured">
                                <div className="wp-pricing-badge">Most Popular</div>
                                <div className="wp-pricing-header">
                                    <h3>WordPress Pro</h3>
                                    <p className="wp-pricing-tagline">Advanced Hosting for Growing Websites</p>
                                </div>
                                <div className="wp-pricing-price">
                                    <span className="wp-price-amount">$8.99</span>
                                    <span className="wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 3 Separate Websites</strong></li>
                                    <li><i className="las la-check"></i> 15GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> WAF & Protection Against Malware</li>
                                    <li><i className="las la-check"></i> Anti-Spam Protection for Email</li>
                                    <li><i className="las la-check"></i> Free SSL for All Domains</li>
                                    <li><i className="las la-check"></i> Cloudflare Integration</li>
                                    <li><i className="las la-check"></i> 1-Click WordPress Install</li>
                                    <li><i className="las la-check"></i> Automatic WordPress Updates</li>
                                    <li><i className="las la-check"></i> Caching for Faster Page Loads</li>
                                    <li><i className="las la-check"></i> Access to Logs & Statistics</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-pro" 
                                   className="wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Business Plan */}
                            <div className="wp-pricing-card">
                                <div className="wp-pricing-header">
                                    <h3>WordPress Business</h3>
                                    <p className="wp-pricing-tagline">Host multiple client projects under one powerful platform</p>
                                </div>
                                <div className="wp-pricing-price">
                                    <span className="wp-price-amount">$15.99</span>
                                    <span className="wp-price-period">USD / Monthly</span>
                                </div>
                                <ul className="wp-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 10 Websites</strong></li>
                                    <li><i className="las la-check"></i> 40GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (~150k Visits/Month)</li>
                                    <li><i className="las la-check"></i> Ideal for WooCommerce Stores</li>
                                    <li><i className="las la-check"></i> WordPress Multisite Support</li>
                                    <li><i className="las la-check"></i> Perfect for Agencies</li>
                                    <li><i className="las la-check"></i> Large Projects Support</li>
                                    <li><i className="las la-check"></i> Priority Support</li>
                                    <li><i className="las la-check"></i> All Pro Features Included</li>
                                    <li><i className="las la-check"></i> Advanced Security Features</li>
                                    <li><i className="las la-check"></i> Reliable Partner for Agencies</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/wordpress-hosting/wordpress-business" 
                                   className="wp-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Managed Services Section */}
                <section className="wp-managed">
                    <div className="container">
                        <div className="wp-section-header">
                            <h2>Fully Managed WordPress</h2>
                            <p>We handle the technical stuff, you focus on content</p>
                        </div>
                        <div className="wp-managed-grid">
                            <div className="wp-managed-card">
                                <i className="las la-tools"></i>
                                <h3>Automatic Maintenance</h3>
                                <p>WordPress core, plugin, and theme updates handled automatically</p>
                            </div>
                            <div className="wp-managed-card">
                                <i className="las la-search"></i>
                                <h3>Malware Scanning</h3>
                                <p>Daily security scans to detect and remove threats</p>
                            </div>
                            <div className="wp-managed-card">
                                <i className="las la-tachometer-alt"></i>
                                <h3>Performance Monitoring</h3>
                                <p>24/7 monitoring to ensure optimal site performance</p>
                            </div>
                            <div className="wp-managed-card">
                                <i className="las la-redo"></i>
                                <h3>Easy Staging</h3>
                                <p>Test changes safely in a staging environment</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="wp-cta">
                    <div className="container">
                        <div className="wp-cta-content">
                            <h2>Ready to Launch Your WordPress Site?</h2>
                            <p>Get started with optimized WordPress hosting today</p>
                            <div className="wp-cta-buttons">
                                <Link to="/pricing" className="btn-wp btn-light">
                                    View Pricing Plans
                                </Link>
                                <Link to="/contact1" className="btn-wp btn-outline">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default WordPressHosting;

